import { useState, useCallback, useMemo, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import { useChatStore } from '@stores/chat.store';
import { useAdvertiserStore } from '@stores/advertiser';
import { useAnalytics } from '@hooks/analytics';
import { useSellerPhone } from '@hooks/api/seller-phone';
import { CHAT_CONVERSATION_TYPES } from '@/utils/constants';

import { HEADER_STRINGS } from '../../utils/header.constants';
import { formatSelfPhone, resolvePhone } from './utils/user-contact.helpers';

export const useUserContactLogic = () => {
	const storeChatPhone = useChatStore((s) => s.phone);
	const conversationActive = useChatStore((s) => s.conversation.active);
	const conversationChatType = useChatStore((s) => s.conversation.chatType);
	const messageListLoading = useChatStore((s) => s.messageList.loading);
	const messageListFetched = useChatStore((s) => s.messageList.fetched);
	const messageListSource = useChatStore((s) => s.messageList.source);
	const storeSelectedTabIndex = useChatStore((s) => s.tabs.conversation);
	const advertiser = useAdvertiserStore(useShallow((s) => ({ source: s.source, fetched: s.fetched, loading: s.loading, error: s.error })));
	const { trackEvent } = useAnalytics();
	const { fetchUserPhone } = useSellerPhone();

	const oppositeType = conversationChatType === CHAT_CONVERSATION_TYPES.purchase
		? CHAT_CONVERSATION_TYPES.Sale
		: CHAT_CONVERSATION_TYPES.Purchase;
	const conversationType = conversationChatType ? oppositeType : undefined;

	const email = conversationType ? (conversationActive?.[`userEmail${conversationType}`] ?? false) : false;

	const isPhoneLoading = messageListLoading || storeChatPhone.loading;
	const phone = useMemo(() => resolvePhone(storeChatPhone, isPhoneLoading), [storeChatPhone, isPhoneLoading]);

	const activeChatId = conversationActive?.chatId;
	const [requestedChatId, setRequestedChatId] = useState<string | undefined>(undefined);

	useEffect(() => {
		if (messageListLoading) useChatStore.getState().resetPhone();
	}, [messageListLoading]);

	useEffect(() => {
		useChatStore.getState().resetPhone();
	}, [activeChatId]);

	const fetchPhone = useCallback(() => {
		const retorno = advertiser?.source?.Retorno;
		if (!retorno) return;

		const store = useChatStore.getState();
		const chatTypePurchase = conversationChatType === CHAT_CONVERSATION_TYPES.purchase;
		const selfId = `PF:${retorno.Id}`;
		const lastMessage = messageListSource?.[messageListSource.length - 1];
		const otherIdKey = `userId${chatTypePurchase ? CHAT_CONVERSATION_TYPES.Sale : CHAT_CONVERSATION_TYPES.Purchase}`;
		const otherId = lastMessage?.chat?.[otherIdKey as keyof typeof lastMessage.chat];
		const chatId = conversationActive?.chatId ?? '';

		if (!otherId) {
			store.setPhoneRejected('');
			return;
		}

		if (selfId === otherId) {
			store.setPhoneLoading(chatId);
			store.setPhoneFulfilled(chatId, formatSelfPhone(String(retorno.DDDPrincipal ?? ''), String(retorno.TelefonePrincipal ?? '')));
			return;
		}

		fetchUserPhone({
			chatId,
			userIdTo: otherId,
			userIdFrom: selfId,
			chatType: String(chatTypePurchase ? CHAT_CONVERSATION_TYPES.purchaseCodeService : CHAT_CONVERSATION_TYPES.saleCodeService),
		});
	}, [messageListSource, conversationChatType, conversationActive, advertiser, fetchUserPhone]);

	const showPhone = !!activeChatId && requestedChatId === activeChatId && !messageListLoading;
	const tabName = storeSelectedTabIndex ? 'venda' : 'compra';

	const showUserPhoneHandler = useCallback(() => {
		trackEvent('verTelefoneChat', {
			eventName: 'verTelefoneChat',
			pageName: `/webmotors/garagem/novo-chat/${tabName}`,
			pageType: `chat-${tabName}`,
			flowType: 'garagem',
			tier: 3,
			recurringEvent: true,
		});
		fetchPhone();
		setRequestedChatId(activeChatId);
	}, [fetchPhone, trackEvent, tabName, activeChatId]);

	const emailHref = email ? `mailto:${email}` : undefined;

	const phoneHref = storeChatPhone.fetched && typeof storeChatPhone.source === 'string' && storeChatPhone.source.length
		? `tel:0${storeChatPhone.source.replaceAll(/\D/g, '')}`
		: undefined;

	const phoneLinkProps = storeChatPhone.source
		? { as: 'a' as React.ElementType, href: phoneHref }
		: {};

	return {
		email,
		phone,
		isPhoneLoading,
		showPhone,
		emailHref,
		phoneLinkProps,
		phoneLoading: storeChatPhone.loading ? 1 : undefined,
		phoneError: storeChatPhone.error ? 1 : undefined,
		phoneTitle: storeChatPhone.fetched && storeChatPhone.source ? HEADER_STRINGS.PHONE_TITLE : undefined,
		showPhoneClickHandler: messageListFetched ? showUserPhoneHandler : undefined,
		showPhoneLabel: messageListFetched,
	};
};
